import { useMemo, useState, useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAllTeams } from '@/store/slices/teams/selectors';
import { getUserId } from '@/utils/helpers/helpers';
import { CopilotAuthResult, CopilotTeamContext } from '@/types/copilot';
import { copilotService } from '@/api/services/copilotService';

/**
 * Hook to determine if the current user has access to copilot functionality
 * based on their team memberships and copilot assignments
 * 
 * @param useBackendApi - If true, will attempt to use backend API for authorization
 */
export const useCopilotAuth = (useBackendApi = false): CopilotAuthResult => {
  const currentUserId = getUserId();
  const teams = useAppSelector(selectAllTeams);
  const [backendAuth, setBackendAuth] = useState<CopilotAuthResult | null>(null);
  const [isCheckingBackend, setIsCheckingBackend] = useState(false);
  
  // Backend API call effect
  useEffect(() => {
    if (useBackendApi && currentUserId && !isCheckingBackend) {
      setIsCheckingBackend(true);
      copilotService.getUserAccessibleCopilots()
        .then(response => {
          setBackendAuth({
            hasAccess: response.data.copilots.length > 0,
            isLoading: false,
            accessibleCopilots: response.data.copilots,
            userTeams: response.data.userTeams,
          });
        })
        .catch(error => {
          console.warn('Backend copilot auth failed, falling back to frontend logic:', error);
          setBackendAuth(null);
        })
        .finally(() => {
          setIsCheckingBackend(false);
        });
    }
  }, [useBackendApi, currentUserId, isCheckingBackend]);
  
  const frontendAuth = useMemo(() => {
    // If no user ID, user is not authenticated
    if (!currentUserId) {
      return {
        hasAccess: false,
        isLoading: false,
        accessibleCopilots: [],
        userTeams: [],
        error: 'User not authenticated'
      };
    }

    // Check if teams are still loading
    if (!teams || teams.length === 0) {
      return {
        hasAccess: false,
        isLoading: true,
        accessibleCopilots: [],
        userTeams: [],
      };
    }

    // Find teams where the current user is a member
    const userTeams = teams.filter(team => {
      if (team.members && Array.isArray(team.members)) {
        return team.members.includes(currentUserId);
      }
      if (team.teamMembers && Array.isArray(team.teamMembers)) {
        return team.teamMembers.some(member => member.id === currentUserId);
      }
      return false;
    });

    // For now, if user is part of any team, they have copilot access
    // This can be refined later when we have copilot-team relationships
    const hasAccess = userTeams.length > 0;

    return {
      hasAccess,
      isLoading: false,
      accessibleCopilots: [], // Will be populated when copilot profiles are integrated
      userTeams,
    };
  }, [currentUserId, teams]);

  // Return backend auth if available and successful, otherwise frontend auth
  if (useBackendApi) {
    if (isCheckingBackend) {
      return {
        ...frontendAuth,
        isLoading: true
      };
    }
    return backendAuth || frontendAuth;
  }

  return frontendAuth;
};

/**
 * Hook to check if user has access to a specific copilot
 */
export const useCopilotAccessCheck = (copilotId?: string): boolean => {
  const { hasAccess, accessibleCopilots } = useCopilotAuth();
  
  if (!copilotId || !hasAccess) {
    return false;
  }

  // TODO: Check specific copilot access once copilot profiles are integrated
  // return accessibleCopilots.some(copilot => copilot.id === copilotId);
  
  // For now, return general access
  return hasAccess;
};

/**
 * Hook to get user's team-based copilot context
 */
export const useCopilotTeamContext = (): CopilotTeamContext => {
  const { userTeams, accessibleCopilots, hasAccess } = useCopilotAuth();
  
  const teamContext = useMemo((): CopilotTeamContext => {
    if (!hasAccess) {
      return {
        primaryTeam: null,
        allTeams: [],
        teamCopilots: {},
        hasMultipleTeams: false
      };
    }

    const primaryTeam = userTeams[0] || null;
    const hasMultipleTeams = userTeams.length > 1;
    
    // TODO: Create team-copilot mapping once copilot profiles are integrated
    const teamCopilots: Record<string, any[]> = {};

    return {
      primaryTeam,
      allTeams: userTeams,
      teamCopilots,
      hasMultipleTeams
    };
  }, [userTeams, hasAccess]);

  return teamContext;
};